import { Button, Callout, Modal } from '@dev-spendesk/grapes';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { requestMfaProcedureSuccess } from 'common/components/ModalMFA/actions';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import mobileImage from 'modules/cards/components/assets/mobile.png';
import { scaProcedureErrors } from 'modules/cards/models/sca';
import type { RecoveryShareMethod } from 'modules/members/components/ConfirmRequesterIdentityModal/ConfirmRequesterIdentityModal';
import { RecoveryFactorModal } from 'modules/members/components/RecoveryFactorModal/RecoveryFactorModal';
import { useInitiateRecoveryFactorScaMutation } from 'modules/members/hooks/useInitiateRecoveryFactorScaMutation';
import { useInitiateRecoveryFactorScaOperationStatusQuery } from 'modules/members/hooks/useInitiateRecoveryFactorScaOperationStatusQuery';
import { AlternateAuthenticationMethodModal } from 'src/auth/modules/StrongCustomerAuthentication/components/AlternateAuthenticationMethodModal/AlternateAuthenticationMethodModal';
import type { AuthenticationFactor } from 'src/auth/modules/StrongCustomerAuthentication/models/factors';

type Props = {
  factors: Array<AuthenticationFactor>;
  member: { id: string; email: string };
  onCancel: () => void;
  recoveryShareMethod: RecoveryShareMethod;
};

export const RecoveryFactorScaModal = ({
  factors,
  member,
  onCancel,
  recoveryShareMethod,
}: Props) => {
  const { t } = useTranslation('global');

  const companyId = useCompanyId();
  const dispatch = useDispatch();

  // FIXME: we should use the type from the Open API schema
  const [selectedAuthenticationMethod, setSelectedAuthenticationMethod] =
    useState<'sca' | 'mfa' | 'totp'>('sca');

  const [procedureId, setProcedureId] = useState<string | undefined>(undefined);
  const [alternateMethodProcedureId, setAlternateMethodProcedureId] = useState<
    string | undefined
  >(undefined);
  const [
    showAlternateAuthenticationMethodModal,
    setShowAlternateAuthenticationMethodModal,
  ] = useState(false);
  const [procedureFailedReason, setProcedureFailedReason] = useState<
    'initializationError' | 'unknown' | 'timeout' | 'rejected' | undefined
  >(undefined);

  const { mutateAsync: initiateRecoveryFactorSca, isError } =
    useInitiateRecoveryFactorScaMutation();

  // Initiate SCA on mount
  useEffect(() => {
    const initiateRecoveryFactorScaRequest = async () => {
      try {
        const response = await initiateRecoveryFactorSca({
          companyId,
          userId: member.id,
          authenticationMethod: 'sca',
        });

        setProcedureId(response.procedureId);
      } catch {
        setProcedureFailedReason('initializationError');
      }
    };

    initiateRecoveryFactorScaRequest();
  }, []);

  const nonDefaultFactors = factors
    .filter((f) => !f.isDefault)
    .filter((f) => f.type !== 'EMAIL');

  const { initiateRecoveryFactorOutput } =
    useInitiateRecoveryFactorScaOperationStatusQuery({
      authenticationMethod: selectedAuthenticationMethod,
      companyId,
      procedureId,
      shareMethod: recoveryShareMethod === 'email' ? 'email' : undefined,
      userId: member.id,
    });

  const handleUseAlternateMethod = () => {
    setShowAlternateAuthenticationMethodModal(true);
  };

  const handleAlternateAuthenticationMethodModalClose =
    async (): Promise<void> => {
      onCancel();
    };

  const handleScaVerified = async (): Promise<void> => {
    setProcedureId(alternateMethodProcedureId);
  };

  const handleAlternateAuthenticationMethodSelected = async (
    selectedAuthenticationFactor: AuthenticationFactor,
  ) => {
    // FIXME: we should use the type from the Open API schema
    let authenticationMethod: 'sca' | 'mfa' | 'totp';
    switch (selectedAuthenticationFactor.type) {
      case 'TOTP':
        authenticationMethod = 'totp';
        break;
      case 'SMS':
      case 'EMAIL':
        authenticationMethod = 'mfa';
        break;
      default:
        authenticationMethod = 'sca';
        break;
    }

    try {
      const { factorId, procedureId: createdProcedureId } =
        await initiateRecoveryFactorSca({
          companyId,
          userId: member.id,
          authenticationMethod,
        });

      setSelectedAuthenticationMethod(authenticationMethod);

      if (authenticationMethod === 'sca') {
        setProcedureId(createdProcedureId);
      } else {
        setAlternateMethodProcedureId(createdProcedureId);
        setProcedureId(undefined);
      }
      if (authenticationMethod === 'mfa' && factorId) {
        // Dispatching is only used for the SMS fallback flow.
        dispatch(
          requestMfaProcedureSuccess({
            id: createdProcedureId,
            factorId,
          }),
        );
      }
    } catch {
      setProcedureFailedReason('initializationError');
    }
  };

  if (initiateRecoveryFactorOutput) {
    const props =
      recoveryShareMethod === 'manual'
        ? {
            onCancel,
            recoveryShareMethod: 'manual' as const,
            recoveryCode: initiateRecoveryFactorOutput.backupCode as string,
          }
        : {
            onCancel,
            recoveryShareMethod: 'email' as const,
            memberEmail: member.email,
          };

    return <RecoveryFactorModal {...props} />;
  }

  if (showAlternateAuthenticationMethodModal) {
    return (
      <AlternateAuthenticationMethodModal
        factors={nonDefaultFactors}
        flow="standard"
        isOpen
        onAlternateAuthenticationMethodSelected={(selectedFactor) =>
          handleAlternateAuthenticationMethodSelected(selectedFactor)
        }
        onClose={handleAlternateAuthenticationMethodModalClose}
        onScaVerified={handleScaVerified}
        operationId={alternateMethodProcedureId}
      />
    );
  }

  return (
    <Modal
      illustration={<img src={mobileImage} alt="" />}
      illustrationHeight="400px"
      title={t('cards.sensitiveOperationScaModal.title')}
      isOpen
      onClose={onCancel}
      actions={[
        <Button
          key="cancel"
          variant="secondaryNeutral"
          text={t('misc.cancel')}
          onClick={onCancel}
        />,
        <Button
          key="alternate-method"
          variant="tertiaryBrand"
          text={t('cards.sensitiveOperationScaModal.alternateMethodCta')}
          onClick={handleUseAlternateMethod}
        />,
      ]}
    >
      {!isError && procedureId && (
        <p className="mt-16 mb-16">
          {t('cards.sensitiveOperationScaModal.subtitle')}
        </p>
      )}
      {isError && procedureFailedReason && (
        <Callout
          className="mb-16"
          title={t(scaProcedureErrors[procedureFailedReason])}
          variant="alert"
        />
      )}
    </Modal>
  );
};
